import './App.css';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, Link, Navigate } from "react-router-dom";
import "slick-carousel/slick/slick.css"; 
import "slick-carousel/slick/slick-theme.css";
import 'bootstrap/dist/css/bootstrap.min.css';
import Header from "./component/header/Header";
import Footer from './component/footer/Footer.jsx';
import React, { Suspense, lazy, useEffect } from "react";
import { useCart } from './context/CartContext';
import ErrorBoundary from './components/ErrorBoundary';
import Swal from 'sweetalert2';
import { HelmetProvider, Helmet } from 'react-helmet-async';

const Home = lazy(() => import("./component/home/Home"));
const Instructors = lazy(() => import("./component/instructors/Instructors"));
const SingleInstructor = lazy(() => import("./component/SingleInstructors/SingleInstructors.jsx"));
const SingleCourse = lazy(() => import("./component/singlecourse/SingleCourse.jsx"));
const CourseCatalog = lazy(() => import("./component/CourseCatalog/CourseCatalog.jsx"));
const CourseCatalogDynamic = lazy(() => import("./component/CourseCatalog/CourseCatalogDynamic.jsx"));
const About = lazy(() => import("./component/about/aboutUs.jsx"));
const Login = lazy(() => import("./component/Login/Login.jsx"));
const Register = lazy(() => import("./component/Register/Register.jsx"));
const Contacts = lazy(() => import("./component/contacts/contacts.jsx"));
const TermsAndCondition = lazy(() => import("./component/terms/TermsAndCondition.jsx"));
const Cart = lazy(() => import("./component/cart/Cart.jsx"));
const AuthCallback = lazy(() => import("./component/auth/AuthCallback.jsx"));
const Profile = lazy(() => import("./component/profile/Profile.jsx"));
const EnglishCourseCatalog = lazy(() => import("./component/CourseCatalog/EnglishCourseCatalog.jsx"));

// 404 page — gives bots a proper response instead of a redirect loop.
function NotFound() {
  return (
    <div style={{ textAlign: 'center', padding: '80px 20px' }}>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Page Not Found | Learnovative Academy</title>
      </Helmet>
      <h1 style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>404</h1>
      <p style={{ color: '#666', marginBottom: '2rem' }}>The page you are looking for does not exist.</p>
      <Link to="/" style={{ color: '#005baa', fontWeight: 600 }}>← Return to Homepage</Link>
    </div>
  );
}

// Canonicalize trailing slashes: if the URL ends with / and is not the root,
// strip it so React Router routes always match consistently.
// This prevents /CourseCatalog/ from failing to match the /CourseCatalog route.
const TrailingSlashRedirect = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.pathname !== '/' && location.pathname.endsWith('/')) {
      navigate(location.pathname.slice(0, -1) + location.search + location.hash, { replace: true });
    }
  }, [location, navigate]);

  return <>{children}</>;
};

const AnalyticsWrapper = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { clearCart } = useCart();

  const trackPageView = (path) => {
    window.dataLayer = window.dataLayer || [];

    const isFirstVisit = !sessionStorage.getItem('has_visited');
    if (isFirstVisit) {
      sessionStorage.setItem('has_visited', 'true');
    }

    window.dataLayer.push({
      event: 'virtual_pageview',
      page_path: path,
      page_title: document.title,
      language: localStorage.getItem('i18nextLng') || 'en',
      is_landing_page: isFirstVisit ? 'yes' : 'no' 
    });
  };

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cartStatus = params.get('cart');

    if (cartStatus === 'success') {
      clearCart();
      navigate(location.pathname, { replace: true });
      Swal.fire({
        icon: 'success',
        title: 'Payment Successful!',
        text: 'Your courses have been enrolled successfully.',
        confirmButtonColor: '#005baa',
        timer: 5000,
        timerProgressBar: true,
      });
    } else if (cartStatus === 'cancelled') {
      navigate(location.pathname, { replace: true });
      Swal.fire({
        icon: 'info',
        title: 'Checkout Cancelled',
        text: 'Your cart has been preserved. You can try again anytime.',
        confirmButtonColor: '#005baa',
      });
    }
  }, [location.search, clearCart, navigate]);

  return <>{children}</>;
};

// App Component
function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <Router>
          <AnalyticsWrapper>
            <TrailingSlashRedirect>
              <Header />
            <ErrorBoundary>
              <Suspense fallback={<div className="content-state">Loading page...</div>}>
                <Routes>
                  <Route exact path="/" element={<Home />} />
                  <Route path="/services" element={<Navigate to="/courses" replace />} />
                  <Route exact path="/about" element={<About />} />
                  <Route exact path="/About" element={<About />} />
                  <Route exact path="/about-us" element={<About />} />
                  <Route exact path="/contact" element={<Contacts />} />
                  <Route exact path="/contacts" element={<Contacts />} />
                  <Route exact path="/Contacts" element={<Contacts />} />
                  <Route exact path="/courses" element={<CourseCatalog />} />
                  <Route exact path="/CourseCatalog" element={<CourseCatalog />} />
                  <Route exact path="/courses/english" element={<EnglishCourseCatalog />} />
                  <Route exact path="/EnglishCourses" element={<EnglishCourseCatalog />} />
                  <Route exact path="/courses/german" element={<CourseCatalogDynamic />} />
                  <Route exact path="/CourseCatalogDynamic" element={<CourseCatalogDynamic />} />
                  <Route exact path="/instructors" element={<Instructors />} />
                  <Route exact path="/Instructors" element={<Instructors />} />
                  <Route exact path="/course/:courseSlug" element={<SingleCourse />} />
                  <Route exact path="/SingleCourse/:courseSlug" element={<SingleCourse />} />
                  <Route exact path="/instructor/:instructorID" element={<SingleInstructor />} />
                  <Route exact path="/SingleInstructor/:instructorID" element={<SingleInstructor />} />
                  <Route exact path="/terms" element={<TermsAndCondition />} />
                  <Route exact path="/TermsAndCondition" element={<TermsAndCondition />} />
                  <Route exact path="/login" element={<Login />} />
                  <Route exact path="/Login" element={<Login />} />
                  <Route exact path="/register" element={<Register />} />
                  <Route exact path="/Register" element={<Register />} />
                  <Route exact path="/cart" element={<Cart />} />
                  <Route exact path="/Cart" element={<Cart />} />
                  <Route exact path="/profile" element={<Profile />} />
                  <Route exact path="/Profile" element={<Profile />} />
                  <Route path="/auth/callback" element={<AuthCallback />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </ErrorBoundary>
            <Footer />
            </TrailingSlashRedirect>
          </AnalyticsWrapper>
        </Router>
      </HelmetProvider>
    </div>
  );
}

export default App;
